import { QueryLazyOptions } from "@apollo/client"
import { Dispatch } from "react"

import {
  billingFields,
  giftFields,
  passwordFields,
  shippingFields,
  stripeFields,
  userFields,
  vatFields,
} from "../shared/checkoutFormFields"
import { AuthStateType } from "../../contexts/authContext"
import { FormActionsType, FormStateType } from "../../contexts/formContext"
import {
  Action,
  CartItem,
  CheckoutActionKind,
  Currency,
  State,
} from "../types/checkoutTypes"
import { CheckoutSteps } from "../state"
import { cartItemsParseByType } from "../shared/cartItemsParseByType"

interface ShippingTaxesData {
  CalculateOrderPrice: {
    shippingCost: number
    taxCost: number
    notes: string
  }
}

class CheckoutShippingEvents {
  static handleInitialComponentRender(
    formActions: FormActionsType,
    formState: FormStateType,
    authState: AuthStateType,
    getUserAddresses: (
      options?: QueryLazyOptions<Record<string, any>> | undefined
    ) => void,
    type: "shipping" | "billing",
    isGiftHeadset = false
  ) {
    formActions.setSkipValidationFunction((field: string): boolean => {
      const shippingBillingFields =
        type === "shipping" ? billingFields : shippingFields
      const fieldsToSkip = [
        ...stripeFields,
        ...shippingBillingFields,
        ...userFields,
        ...passwordFields,
        ...vatFields,
        ...giftFields,
      ]
      return fieldsToSkip.includes(field)
    })

    // Only query the API for addresses if the User is logged in
    if (authState.email) {
      getUserAddresses({
        variables: {
          email: authState.email,
        },
      })
    }

    // Default first and last name
    formActions.setFormData({
      shippingFirstName:
        (isGiftHeadset && formState.inputs.giftFirstName?.value) ||
        formState.inputs.shippingFirstName?.value ||
        formState.inputs.firstName?.value ||
        authState.firstName ||
        "",
      shippingLastName:
        (isGiftHeadset && formState.inputs.giftLastName?.value) ||
        formState.inputs.shippingLastName?.value ||
        formState.inputs.lastName?.value ||
        authState.lastName ||
        "",
    })
  }

  static handleReceivedShippingTaxesData(
    loading: boolean,
    errorMessage: string | undefined,
    data: ShippingTaxesData,
    checkoutState: State,
    checkoutDispatch: Dispatch<Action>
  ) {
    if (!data || !data.CalculateOrderPrice) return

    const { shipping } = checkoutState

    const { shippingCost, taxCost, notes } = data.CalculateOrderPrice

    // TODO: Can we eliminate some of these checks?
    if (
      !loading &&
      !errorMessage &&
      shippingCost !== undefined &&
      shippingCost !== null
    ) {
      checkoutDispatch({
        type: CheckoutActionKind.SET_TAXES,
        payload: { amount: taxCost },
      })
      checkoutDispatch({
        type: CheckoutActionKind.SET_SHIPPING,
        payload: { amount: shippingCost },
      })
      checkoutDispatch({
        type: CheckoutActionKind.SET_SHIPPING_NOTES,
        payload: { notes },
      })
      // TODO: Make sure this always works
      // - we might have to wait for the state items above to be updated first
      checkoutDispatch({
        type: CheckoutActionKind.GO_TO_STEP,
        payload: { stepNumber: CheckoutSteps.Payment },
      })
    }
  }

  static getCalculateOrderPriceVariables(
    cartItems: CartItem[],
    currency: Currency,
    checkoutFormState: FormStateType,
    couponCode: string
  ) {
    const [onetime, subs] = cartItemsParseByType(cartItems, currency)

    // TODO: Why do we need this

    // if (
    //   !checkoutFormState?.inputs?.shippingCountry?.value &&
    //   !checkoutFormState?.inputs?.billingCountry?.value
    // ) {
    //   actions.setSalesTaxTotal(undefined)
    //   actions.setShippingTotal(undefined)
    //   return
    // }

    const variables = {
      country:
        checkoutFormState.inputs.shippingCountry.value ||
        checkoutFormState.inputs.billingCountry.value,
      currency,
      city:
        checkoutFormState.inputs.shippingCity.value ||
        checkoutFormState.inputs.billingCity.value,
      postal:
        checkoutFormState.inputs.shippingPostal.value ||
        checkoutFormState.inputs.billingPostal.value,
      province:
        checkoutFormState.inputs.shippingProvince.value ||
        checkoutFormState.inputs.billingProvince.value,
      street:
        checkoutFormState.inputs.shippingStreet.value ||
        checkoutFormState.inputs.billingStreet.value,
      items: [...onetime, ...subs],
      couponCode,
    }

    return variables
  }

  static initGoogleMaps(
    loaded: boolean,
    setLoaded: React.Dispatch<React.SetStateAction<boolean>>
  ) {
    function loadScript(src: string, position: HTMLElement | null, id: string) {
      if (!position) {
        return
      }
      const script = document.createElement("script")
      script.setAttribute("async", "")
      script.setAttribute("id", id)
      script.src = src
      position.appendChild(script)
    }

    if (typeof window !== "undefined" && !loaded) {
      if (!document.querySelector("#google-maps")) {
        loadScript(
          "https://maps.googleapis.com/maps/api/js?key=AIzaSyCaep8yWTtJSpqWVylvW9ob4EDcDVbPLTM&libraries=places&callback=Function.prototype",
          document.querySelector("head"),
          "google-maps"
        )
      }
      setLoaded(true)
    }
  }
}

export default CheckoutShippingEvents
